import { useEffect, useMemo, useState } from 'react';

import {
  headersParcelasContratos as headers,
  valoresExibidosOffCanvasContratos as valoresExibidos,
} from './auxiliarParcela';
import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../components/BoxConteudo';
import CustomButton from '../../../../../components/CustomButton';
import CustomCol from '../../../../../components/CustomCol';
import CustomInputDate from '../../../../../components/CustomInputs/CustomInputDate';
import CustomOffcanvas from '../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../components/CustomRow';
import Input from '../../../../../components/InputNew';
import InputMoney from '../../../../../components/InputNew/InputMoney';
import Select from '../../../../../components/InputNew/Select';
import Log from '../../../../../components/Log/Log';
import Tabela from '../../../../../components/Tabela';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import {
  EnumOption,
  LogData,
  LogInterface,
} from '../../../../../modules/types/types';
import { getAllObjetoByContrato } from '../../../../../services/contratos/objetoContratoService';
import {
  getAllParcelaByContrato,
  handleCreateParcelaContrato,
  handleDeleteParcelaContrato,
  handleUpdateParcelaContrato,
} from '../../../../../services/contratos/parcelaContratoService';
import { isHttpError } from '../../../../../services/httpClient';
import theme from '../../../../../theme';
import { ENTITY } from '../../../../../utils/entityNames';
import {
  formataNomeMeioSiglas,
  formatDate,
  formatDateSimp,
  formatValorDinheiro,
  sanitizarForm,
} from '../../../../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../../../../utils/swal';
import { IObjetoContrato } from '../objeto';

interface ParcelaContratoProps {
  fichaID: string;
  userID: number;
  btDisable?: boolean;
  contratoData: any;
  onDataChange: () => void;
}

export interface IParcelaContrato extends Partial<LogInterface> {
  id?: number;
  usuarioId: number;
  produtoFarmaceutico: string;
  objetoId: number;
  objeto?: IObjetoContrato | null;
  fatorEmbalagem?: string;
  qtdEmbalagem: string;
  contratoId: number;
  contrato?: any;
  nParcela: number;
  dataPrevista: string;
  qtdAdministrativa: string;
  qtdEstrategico: string;
  qtdJudicial: string;
  qtdDoada: string;
  qtdTotal: string;
  valorParcela: string;
  valorTotal: string;
  dataPrimeiraEntrega: string;
  dataUltimaEntrega: string;
  atrasoDias: string;
  qtdEntregue: string;
  qtdEntregar: string;
  percentualEntregue: string;
  valorUnitario: string;
  contratoPercentual: string;
  valorEmpenhado: string;
  valorEmpenhar: string;
  empenhadoPercentual: string;
  observacoes: string;
}

const tbEntity = ENTITY.ENTITY_Contrato_Parcela;

export const ParcelaContrato = ({ ...props }: ParcelaContratoProps) => {
  const [updating, setUpdating] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showModalObjeto, setShowModalObjeto] = useState<boolean>(false);

  // Data
  const [data, setData] = useState<IParcelaContrato[]>();
  const [logData, setLogData] = useState<LogData>();
  const [numeroProximaParcela, setNumeroProximaParcela] = useState<number>(1);
  const [objetoNumero, setObjetoNumero] = useState<number>(0);
  const [objetoData, setObjetoData] = useState<IObjetoContrato[]>();
  const [objetoOptions, setObjetoOptions] = useState<EnumOption[]>([
    { value: '0', label: 'Não informado' },
  ]);
  const [form, setForm] = useState<IParcelaContrato>({
    id: 0,
    nParcela: 0,
    contratoId: 0,
    objetoId: 0,
    produtoFarmaceutico: '',
    qtdAdministrativa: '',
    qtdEstrategico: '',
    qtdJudicial: '',
    qtdDoada: '',
    qtdTotal: '',
    fatorEmbalagem: '',
    qtdEmbalagem: '',
    dataPrevista: '',
    valorParcela: '',
    valorTotal: '',
    dataPrimeiraEntrega: '',
    dataUltimaEntrega: '',
    atrasoDias: '',
    qtdEntregue: '',
    qtdEntregar: '',
    percentualEntregue: '',
    valorUnitario: '',
    contratoPercentual: '',
    valorEmpenhado: '',
    valorEmpenhar: '',
    empenhadoPercentual: '',
    observacoes: '',

    contrato: null,
    objeto: null,
    usuarioId: props.userID,
  });

  // Fetch
  const fetchObjetos = async () => {
    try {
      if (props.fichaID !== 'undefined') {
        const objetosData = await getAllObjetoByContrato(props.fichaID);
        setObjetoData(objetosData);

        if (!objetosData || objetosData.length === 0) {
          throw new Error('Nenhum objeto encontrado.');
        }

        const newOptions = objetosData.map((item: IObjetoContrato, index) => ({
          value: item.id?.toString() ?? (index + 1).toString(),
          label: `Objeto ${item.nObjeto} - ${item.produtoDAF?.produtoFarmaceutico}`,
        }));

        const uniqueOptions = [
          { value: '0', label: 'Não informado' }, // sempre presente
          ...Array.from(
            new Map(newOptions.map((item) => [item.value, item])).values(),
          ),
        ];

        setObjetoOptions(uniqueOptions);
      }
    } catch (error) {
      console.error('Erro ao obter produtos:', error);
      setObjetoOptions([]);
    }
  };

  const fetchData = async () => {
    try {
      const parcelas = await getAllParcelaByContrato(props.fichaID);
      setData(parcelas);
    } catch (error) {
      console.error('Erro ao obter dados: ', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [props.fichaID]);

  useEffect(() => {
    fetchObjetos();
  }, [props.contratoData, props.fichaID]);

  useEffect(() => {
    handleAtualizarProxParcela();
  }, [data]);

  useMemo(() => {
    const objeto = objetoData?.find((item) => item.id == form.objetoId);
    if (objeto) {
      const produto = objeto.produtoDAF?.produtoFarmaceutico ?? '';
      const numeroObjeto = objeto.nObjeto;
      setForm({
        ...form,
        objeto: objeto,
        objetoId: objeto.id ?? 0,
        produtoFarmaceutico: produto,
      });
      setObjetoNumero(numeroObjeto);
    }
  }, [form.objetoId]);

  // Funções handle
  const handleClearForm = () => {
    setForm({
      ...form,
      id: 0,
      nParcela: 0,
      contratoId: 0,
      dataPrevista: '',
      qtdAdministrativa: '',
      qtdEstrategico: '',
      qtdJudicial: '',
      qtdDoada: '',
      qtdTotal: '',
      valorParcela: '',
      valorTotal: '',
      dataPrimeiraEntrega: '',
      dataUltimaEntrega: '',
      atrasoDias: '',
      qtdEntregue: '',
      qtdEntregar: '',
      percentualEntregue: '',
      valorUnitario: '',
      contratoPercentual: '',
      valorEmpenhado: '',
      valorEmpenhar: '',
      empenhadoPercentual: '',
      fatorEmbalagem: '',
      observacoes: '',
      usuarioId: props.userID,
    });
    setLogData({
      ...logData,
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleClearFormObjeto = () => {
    setForm({
      ...form,
      objetoId: 0,
    });
  };

  const handleAtualizarProxParcela = () => {
    if (data && data.length > 0) {
      const numeroItemMax = data[data.length - 1].nParcela;
      setNumeroProximaParcela(numeroItemMax + 1);
    } else {
      setNumeroProximaParcela(1);
    }
  };

  // Tabela
  const tableData =
    data?.map((item: IParcelaContrato) => {
      return {
        id: item.id,
        nObjeto: item.objeto?.nObjeto ?? '-',
        nParcela: item.nParcela,
        dataPrevista: formatDateSimp(item.dataPrevista),
        produtoDAF:
          item.objeto?.produtoDAF?.produtoFarmaceutico ?? 'Não informado',
        qtdTotal: item.qtdTotal ?? 'Não informado',
        valorParcela: item.valorParcela
          ? `R$ ${formatValorDinheiro(parseFloat(item.valorParcela).toFixed(2))}`
          : '-',
      };
    }) || [];

  const offcanvasData =
    data?.map((item: IParcelaContrato) => {
      return {
        id: item.id,
        dataRegistro:
          formatDate(item.dataRegistro ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioRegistro?.nomeCompleto ?? ''),
        dataUltimaAtualizacao:
          formatDate(item.dataUltimaAtualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(item.usuarioAtualizacao?.nomeCompleto ?? ''),

        dataUltimaVisualizacao:
          formatDate(item.dataUltimaVisualizacao ?? '') +
          ' - ' +
          formataNomeMeioSiglas(
            item.usuarioUltimaVisualizacao?.nomeCompleto ?? '',
          ),
        nEdicoes: item.nEdicoes,
        nVisualizacoes: item.nVisualizacoes,
        separadorLog: '',
        nParcela: item.nParcela,
        nObjeto: item.objeto?.nObjeto ?? '-',
        produtoDAF: (
          <b>
            {item.objeto?.produtoDAF?.produtoFarmaceutico ?? 'Não informado'}
          </b>
        ),
        valorParcela: formatValorDinheiro(item.valorParcela) ?? '-',
        qtdTotal: item.qtdTotal ?? '-',
        valorTotal: formatValorDinheiro(item.valorTotal) ?? '-',
        separador: '',
        observacoes: item.observacoes,
      };
    }) || [];

  //Funções Handle
  const handleShowModal = () => {
    setShowModal((prev) => !prev);
    handleClearFormObjeto();
    handleClearForm();
  };

  const handleShowModalObjeto = () => {
    setShowModalObjeto((prev) => !prev);
  };

  const handleClickInserirNovo = (objetoId: number) => {
    handleClearForm();
    setUpdating(false);
    handleShowModal();
  };

  const handleClickInserirParcela = () => {
    handleShowModalObjeto();
  };

  const handleRowClick = (id: string) => {
    handleShowModal();
    setUpdating(true);
    const filteredItem = data?.find((item) => item.id == parseInt(id, 10));
    console.log('filteredItem', filteredItem);
    if (filteredItem) {
      setForm({
        id: filteredItem.id,
        nParcela: filteredItem.nParcela,
        objeto: filteredItem.objeto,
        fatorEmbalagem: filteredItem.objeto?.fatorEmbalagem ?? '',
        qtdEmbalagem: (
          Number(filteredItem.valorTotal) /
          Number(filteredItem.objeto?.fatorEmbalagem)
        ).toString(),
        valorTotal: filteredItem.valorTotal,
        qtdTotal: filteredItem.qtdTotal,
        qtdDoada: filteredItem.qtdDoada,
        qtdAdministrativa: filteredItem.qtdAdministrativa,

        dataPrimeiraEntrega: filteredItem.dataPrimeiraEntrega || '',
        dataUltimaEntrega: filteredItem.dataUltimaEntrega || '',
        atrasoDias: filteredItem.atrasoDias || '',
        qtdEntregue: filteredItem.qtdEntregue || '',
        qtdEntregar: filteredItem.qtdEntregar || '',
        percentualEntregue: filteredItem.percentualEntregue || '',
        valorUnitario: filteredItem.objeto?.valorUnitario || '',
        contratoPercentual: filteredItem.contratoPercentual || '',
        valorEmpenhado: filteredItem.valorEmpenhado || '',
        valorEmpenhar: filteredItem.valorEmpenhar || '',
        empenhadoPercentual: filteredItem.empenhadoPercentual || '',
        observacoes: filteredItem.observacoes ?? 'Não informado',

        // chave usuário
        usuarioId: props.userID,
        objetoId: filteredItem.objeto?.id ?? 0,
        produtoFarmaceutico:
          filteredItem.objeto?.produtoDAF?.produtoFarmaceutico ?? '',
        contratoId: filteredItem.contrato?.id,
        dataPrevista: filteredItem.dataPrevista
          ? new Date(filteredItem.dataPrevista).toISOString().split('T')[0]
          : '',
        qtdEstrategico: filteredItem.qtdEstrategico,
        qtdJudicial: filteredItem.qtdJudicial,
        valorParcela:
          filteredItem.valorParcela && filteredItem.valorParcela != null
            ? parseFloat(filteredItem.valorParcela).toFixed(4)
            : '',
      });
      setLogData({
        id: filteredItem.id ?? 0,
        usuarioRegistro: filteredItem.usuarioRegistro?.nomeCompleto || '',
        usuarioAtualizacao: filteredItem.usuarioAtualizacao?.nomeCompleto || '',
        dataRegistro: filteredItem.dataRegistro || '',
        dataAtualizacao: filteredItem.dataUltimaAtualizacao || '',
        nEdicoes: filteredItem.nEdicoes || 0,
      });
    }
  };

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target as HTMLInputElement;

    const newValue = value === '' ? 0 : parseInt(value, 10);

    if (name == 'objeto') {
      const id = objetoOptions.find(
        (option) => option.value.toString() === value,
      );
      if (id) {
        setForm({
          ...form,
          objetoId: newValue,
        });
      }
    } else if (name === 'nParcela') {
      setNumeroProximaParcela(newValue);
      setForm({
        ...form,
        nParcela: newValue,
      });
    } else {
      setForm((prev) => {
        const updatedForm = { ...prev, [name]: value };

        return updatedForm;
      });
    }
  };

  const handleQuantidadeTotal = (): number => {
    const qtdDoada = Number(form.qtdDoada);
    const qtdContratada = Number(form.qtdAdministrativa);
    const qtdJudicial = Number(form.qtdJudicial);
    const qtdEstrategico = Number(form.qtdEstrategico);

    return qtdContratada + qtdJudicial + qtdEstrategico + qtdDoada;
  };

  const handleQuantidadeEmbalagens = (): number => {
    const qtdDoada = Number(form.qtdDoada);
    const qtdContratada = Number(form.qtdAdministrativa);
    const qtdJudicial = Number(form.qtdJudicial);
    const qtdEstrategico = Number(form.qtdEstrategico);
    const fatorEmbalagem = Number(form.objeto?.fatorEmbalagem) || 1;
    return (
      (qtdContratada + qtdJudicial + qtdEstrategico + qtdDoada) / fatorEmbalagem
    );
  };

  const validateForm = (form: IParcelaContrato): boolean => {
    const erros: string[] = [];

    if (!form.valorParcela) {
      erros.push('<b>Valor da Parcela</b> é obrigatório.');
    }

    if (!form.objetoId) {
      erros.push('<b>Objeto do Contrato</b> é obrigatório.');
    }

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }

    return true;
  };

  const formatForm = (form: IParcelaContrato) => {
    const formAjustado = {
      ...form,
      usuarioId: Number(form.usuarioId),
      objetoId: parseInt(form.objetoId.toString(), 10),
      contratoId: Number(props.fichaID),
      nParcela: numeroProximaParcela,
      valorParcela: parseFloat(form.valorParcela),
      qtdAdministrativa: Number(form.qtdAdministrativa),
      qtdJudicial: Number(form.qtdJudicial),
      qtdEstrategico: Number(form.qtdEstrategico),
      qtdDoada: Number(form.qtdDoada),
    };

    return formAjustado;
  };

  const handleCreate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);
      console.log('formToSend', formToSend);
      await handleCreateParcelaContrato(formToSend);

      swalSave();
      fetchData();

      setTimeout(() => {
        handleShowModal();
        props.onDataChange();
      }, 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao salvar registro:', err);
    }
  };

  const handleUpdate = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido.');
      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);
      console.log('formToSend', formToSend);
      const response = await handleUpdateParcelaContrato(formToSend);

      swalAtualizacao('Sucesso', response.data.message);
      fetchData();

      setTimeout(() => {
        handleShowModal();
        props.onDataChange();
      }, 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao atualizar registro:', err);
    }
  };

  const handleDelete = async () => {
    const result = await swalDelete();
    if (!result) return swalErro('A operação falhou.');
    try {
      if (form.id) {
        await handleDeleteParcelaContrato(form.id);
      } else return;
      swalDeleteConfirm();
      fetchData();

      setTimeout(() => {
        handleShowModal();
        props.onDataChange();
      }, 2000);
    } catch (err: any) {
      if (isHttpError(err)) swalErro(err.response?.data.message);
      console.error('Erro ao excluir registro:', err);
    }
  };

  return (
    <BoxConteudo
      boxTitulo="Parcelas do Contrato"
      fechado={!(props.fichaID !== 'undefined')}
    >
      <CustomRow>
        <CustomCol width="100%">
          <Tabela
            headers={headers}
            dataTable={tableData}
            displayValues={valoresExibidos}
            onRowClick={handleRowClick}
            dataOffCanvas={offcanvasData}
            showPagination={false}
          />
        </CustomCol>
      </CustomRow>
      <CustomCol className="d-flex justify-content-end">
        <CustomButton
          label="Nova Parcela"
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
          onClick={handleClickInserirParcela}
          disabled={props.fichaID == 'undefined' ? true : false}
        />
      </CustomCol>

      {/* Modal Parcela */}
      <CustomOffcanvas
        title={'Parcelas do contrato'}
        show={showModal}
        onClose={handleShowModal}
        width="70vw"
      >
        <WorkspaceFicha>
          <Log {...logData} entity={tbEntity} id={Number(form.id)} />
          <BoxConteudo boxTitulo={'Parcela do Objeto'}>
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol width="130px">
                <Input
                  name="nParcela"
                  value={updating ? form.nParcela : numeroProximaParcela}
                  id="nParcela"
                  label="Parcela"
                  onChange={handleChange}
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol width="130px">
                <Input
                  name="nObjeto"
                  value={objetoNumero}
                  id="nObjeto"
                  label="Objeto"
                  onChange={handleChange}
                  textAlign="right"
                  disabled
                />
              </CustomCol>
              <CustomCol>
                <Input
                  id="produtoFarmaceutico"
                  name="produtoFarmaceutico"
                  value={form.produtoFarmaceutico}
                  label="Produto Farmacêutico"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="200px">
                <Input
                  name="qtdAdministrativa"
                  label="Qtd Ctd - Administrativo"
                  value={form.qtdAdministrativa}
                  id="qtdAdministrativa"
                  onChange={handleChange}
                  textAlign="right"
                  type="number"
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <Input
                  name="qtdEstrategico"
                  label="Qtd Ctd - Estratégico"
                  value={form.qtdEstrategico}
                  id="qtdEstrategico"
                  onChange={handleChange}
                  type="number"
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <Input
                  name="qtdJudicial"
                  label="Qtd Ctd - Judicial"
                  value={form.qtdJudicial}
                  id="qtdJudicial"
                  onChange={handleChange}
                  type="number"
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol width="180px">
                <Input
                  name="qtdDoada"
                  label="Qtd Doada"
                  value={form.qtdDoada}
                  id="qtdDoada"
                  onChange={handleChange}
                  type="number"
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="qtdTotal"
                  label="Qtd Total"
                  value={handleQuantidadeTotal()}
                  id="qtdTotal"
                  onChange={handleChange}
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol width="13%">
                <Input
                  name="fatorEmbalagem"
                  value={form.objeto?.fatorEmbalagem ?? 0}
                  id="fatorEmbalagem"
                  label="Fator Emb."
                  onChange={handleChange}
                  textAlign="right"
                  disabled
                />
              </CustomCol>
              <CustomCol width="150px">
                <Input
                  name="qtdEmbalagem"
                  label="Qtd Embalagens"
                  value={handleQuantidadeEmbalagens()}
                  id="qtdEmbalagem"
                  onChange={handleChange}
                  textAlign="right"
                  disabled
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="200px">
                <CustomInputDate
                  name="dataPrevista"
                  title="Data de Entrega Prevista"
                  value={form.dataPrevista}
                  onChange={handleChange}
                  required
                />
              </CustomCol>

              <CustomCol width="180px">
                <Input
                  name="dataPrimeiraEntrega"
                  value={form.dataPrimeiraEntrega ?? ''}
                  id="dataPrimeiraEntrega"
                  label="Data 1ª Entrega"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>

              <CustomCol width="180px">
                <Input
                  name="dataUltimaEntrega"
                  value={form.dataUltimaEntrega ?? ''}
                  id="dataUltimaEntrega"
                  label="Data Últ. Entrega"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>

              <CustomCol width="180px">
                <Input
                  name="atrasoDias"
                  value={form.atrasoDias ?? ''}
                  id="atrasoDias"
                  label="Dias de Atraso"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>

              <CustomCol width="180px">
                <Input
                  name="qtdEntregue"
                  value={form.qtdEntregue ?? ''}
                  id="qtdEntregue"
                  label="Qtd Entregue"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>

              <CustomCol>
                <Input
                  name="qtdEntregar"
                  value={form.qtdEntregar ?? ''}
                  id="qtdEntregar"
                  label="Qtd a Entregar"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>

              <CustomCol width="100px">
                <Input
                  name="percentualEntregue"
                  value={form.percentualEntregue ?? ''}
                  id="percentualEntregue"
                  label="% Entregue"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow style={{ justifyContent: 'flex-start' }}>
              <CustomCol width="14%">
                <Input
                  name="valorUnitario"
                  value={
                    formatValorDinheiro(form.objeto?.valorUnitario ?? '') ?? ''
                  }
                  id="valorUnitario"
                  label="Valor Unitário R$"
                  onChange={handleChange}
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol width="16%">
                <InputMoney
                  name="valorParcela"
                  value={form.valorParcela}
                  id="valorParcela"
                  label="Valor Parcela R$"
                  casasDecimais={4}
                  onChange={handleChange}
                  textAlign="right"
                  required
                />
              </CustomCol>
              <CustomCol width="10%">
                <Input
                  name="contratoPercentual"
                  value={form.contratoPercentual}
                  id="contratoPercentual"
                  label="% do Contrato"
                  onChange={handleChange}
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol width="16%">
                <InputMoney
                  name="valorEmpenhado"
                  value={form.valorEmpenhado}
                  id="valorEmpenhado"
                  label="Valor Empenhado R$"
                  casasDecimais={4}
                  onChange={handleChange}
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol width="16%">
                <InputMoney
                  name="valorEmpenhar"
                  value={form.valorEmpenhar}
                  id="valorEmpenhar"
                  label="Valor Empenhar R$"
                  casasDecimais={4}
                  onChange={handleChange}
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol width="10%">
                <Input
                  name="empenhadoPercentual"
                  value={form.empenhadoPercentual}
                  id="empenhadoPercentual"
                  label="% Empenhado"
                  onChange={handleChange}
                  textAlign="right"
                  readonly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <Input
                  id="observacoes"
                  type="textarea"
                  name="observacoes"
                  value={form.observacoes}
                  label="Observações"
                  linhas={3}
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                deleteDisabled={props.btDisable}
                onClickDelete={handleDelete}
                save
                saveDisabled={props.btDisable}
                onClickSave={handleUpdate}
              />
            ) : (
              <WorkspaceBotoes
                inserir
                inserirDisabled={props.btDisable}
                onClickInserir={handleCreate}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>

      {/* Modal Objeto */}
      <CustomOffcanvas
        title={'Objetos do contrato'}
        show={showModalObjeto}
        icon={'edit_document'}
        onClose={handleShowModalObjeto}
        width="70vw"
      >
        <WorkspaceFicha>
          <CustomRow>
            <CustomCol width="130px">
              <Input
                name="objetoId"
                value={form.objetoId}
                id="objetoId"
                label="ID"
                onChange={handleChange}
                textAlign="right"
                disabled
              />
            </CustomCol>
            <CustomCol>
              <Select
                name="objetoId"
                value={form.objetoId}
                options={objetoOptions}
                onChange={handleChange}
                label="Objeto"
              />
            </CustomCol>
          </CustomRow>

          <BarraInferiorBotoes>
            <WorkspaceBotoes
              inserir
              inserirDisabled={!(form.objetoId != 0)}
              onClickInserir={() => {
                handleClickInserirNovo(form.objetoId);
                handleShowModalObjeto();
              }}
            />
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
};
